import { type EligibilityConditions } from "src/generated/hooks/form-service/requests";
import { z } from "zod";

type Comparison = NonNullable<
  EligibilityConditions["expression"]
>["anyOf"][number]["allOf"][number];
type NumericComparison = Extract<Comparison, { kind: "Numeric" }>;
type NumericValue = NumericComparison["left"];

export type CapacityOperator = "lessThan" | "greaterThan" | "between";
export type CapacityUnit = "seats" | "percent";

export type CapacityCondition = {
  kind: "capacity";
  id: string;
  operator: CapacityOperator | "";
  unit: CapacityUnit | "";
  value: string;
  endValue: string;
};

export function emptyCapacityCondition(): CapacityCondition {
  return {
    kind: "capacity",
    id: crypto.randomUUID(),
    operator: "",
    unit: "",
    value: "",
    endValue: "",
  };
}

const requiredMessage = "This field is required";

// A number input can store either a string or a number. Keep one string for validation.
const amountSchema = z.preprocess((value) => {
  if (typeof value === "number") return String(value);
  return value;
}, z.string());

export const capacityConditionSchema = z
  .object({
    kind: z.literal("capacity"),
    id: z.string(),
    operator: z.string().min(1, requiredMessage),
    unit: z.string(),
    value: amountSchema,
    endValue: amountSchema,
  })
  .superRefine((condition, ctx) => {
    const needsValue =
      condition.operator === "lessThan" ||
      condition.operator === "greaterThan" ||
      condition.operator === "between";
    if (!needsValue) return;

    if (condition.unit === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["unit"],
        message: requiredMessage,
      });
    }
    checkAmount(condition.value, condition.unit, "value", ctx);
    if (condition.operator === "between") {
      checkAmount(condition.endValue, condition.unit, "endValue", ctx);
    }
    const start = Number(condition.value);
    const end = Number(condition.endValue);
    if (
      condition.operator === "between" &&
      condition.value !== "" &&
      condition.endValue !== "" &&
      Number.isFinite(start) &&
      Number.isFinite(end) &&
      end < start
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["value"],
        message: "Minimum must be less than or equal to the maximum",
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["endValue"],
        message: "Maximum must be greater than or equal to the minimum",
      });
    }
  });

export function comparisonToCapacityCondition(
  comparison: Comparison
): CapacityCondition | null {
  if (comparison.kind !== "Numeric") return null;
  if (!isSeatsAvailable(comparison.left)) return null;

  if (
    comparison.operator === "LessThan" ||
    comparison.operator === "GreaterThan"
  ) {
    const threshold = readThreshold(comparison.right);
    if (!threshold) return null;
    return {
      kind: "capacity",
      id: crypto.randomUUID(),
      operator: comparison.operator === "LessThan" ? "lessThan" : "greaterThan",
      unit: threshold.unit,
      value: threshold.value,
      endValue: "",
    };
  }

  if (comparison.operator === "Between") {
    const start = readThreshold(comparison.start);
    const end = readThreshold(comparison.end);
    if (!start || !end || start.unit !== end.unit) return null;
    return {
      kind: "capacity",
      id: crypto.randomUUID(),
      operator: "between",
      unit: start.unit,
      value: start.value,
      endValue: end.value,
    };
  }

  return null;
}

export function capacityConditionToComparison(
  condition: CapacityCondition
): NumericComparison {
  const left = {
    source: "Capacity" as const,
    field: "SeatsAvailable" as const,
  };
  if (condition.operator === "between") {
    return {
      kind: "Numeric",
      operator: "Between",
      left,
      start: thresholdValue(condition, condition.value),
      end: thresholdValue(condition, condition.endValue),
    };
  }
  if (
    condition.operator === "lessThan" ||
    condition.operator === "greaterThan"
  ) {
    return {
      kind: "Numeric",
      operator: condition.operator === "lessThan" ? "LessThan" : "GreaterThan",
      left,
      right: thresholdValue(condition, condition.value),
    };
  }
  throw new Error("Capacity condition is incomplete");
}

export function describeCapacityCondition(
  condition: CapacityCondition
): string {
  const start = formatAmount(condition.value, condition.unit);
  const end = formatAmount(condition.endValue, condition.unit);
  const ofCapacity =
    condition.unit === "percent" ? "of total capacity" : "seats";
  if (condition.operator === "between") {
    return `Capacity available is between ${start} and ${end} ${ofCapacity}`;
  }
  if (
    condition.operator === "lessThan" ||
    condition.operator === "greaterThan"
  ) {
    const label =
      condition.operator === "lessThan" ? "less than" : "greater than";
    const unit = start === "1" && ofCapacity === "seats" ? "seat" : ofCapacity;
    return `Capacity available is ${label} ${start} ${unit}`;
  }
  return "Capacity available";
}

function checkAmount(
  value: string,
  unit: string,
  path: "value" | "endValue",
  ctx: z.RefinementCtx
) {
  if (value === "") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [path],
      message: requiredMessage,
    });
    return;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [path],
      message: "Enter a number",
    });
    return;
  }
  if (
    unit === "percent" &&
    (!Number.isInteger(parsed) || parsed < 0 || parsed > 100)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [path],
      message: "Enter a whole number from 0 to 100",
    });
  }
  if (unit === "seats" && (!Number.isInteger(parsed) || parsed < 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [path],
      message: "Enter a whole number that is 0 or greater",
    });
  }
}

function readThreshold(
  value: NumericValue
): { unit: CapacityUnit; value: string } | null {
  if (isNumberLiteral(value)) {
    return { unit: "seats", value: String(value.value) };
  }
  if (isPercentExpression(value)) {
    return { unit: "percent", value: percentFromFraction(value.right.value) };
  }
  return null;
}

function thresholdValue(
  condition: CapacityCondition,
  amount: string
): NumericValue {
  const parsed = Number(amount);
  if (condition.unit === "percent") {
    return {
      source: "Expression",
      operator: "Multiply",
      left: { source: "Capacity", field: "Capacity" },
      right: { source: "Literal", value: parsed / 100 },
    };
  }
  if (condition.unit === "seats") {
    return { source: "Literal", value: parsed };
  }
  throw new Error("Capacity condition is incomplete");
}

function formatAmount(value: string, unit: CapacityUnit | ""): string {
  return unit === "percent" ? `${value}%` : value;
}

function percentFromFraction(value: number): string {
  return String(Math.round(value * 100));
}

function isSeatsAvailable(
  value: NumericValue
): value is { source: "Capacity"; field: "SeatsAvailable" } {
  return value.source === "Capacity" && value.field === "SeatsAvailable";
}

function isNumberLiteral(
  value: NumericValue
): value is { source: "Literal"; value: number } {
  return value.source === "Literal" && typeof value.value === "number";
}

function isPercentExpression(value: NumericValue): value is {
  source: "Expression";
  operator: "Multiply";
  left: { source: "Capacity"; field: "Capacity" };
  right: { source: "Literal"; value: number };
} {
  return (
    value.source === "Expression" &&
    value.operator === "Multiply" &&
    value.left.source === "Capacity" &&
    value.left.field === "Capacity" &&
    value.right.source === "Literal" &&
    typeof value.right.value === "number"
  );
}
